import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BLOG_ARTICLES } from '@/mocks/blog';
import { getAllArticleViews, formatViews } from '@/hooks/useArticleViews';

const CATEGORY_COLORS: Record<string, string> = {
  'Иммунитет': '#ff2d78',
  'Уход за кожей': '#ff6b00',
  'Витамины и БАД': '#00f5ff',
  'Здоровый сон': '#39ff14',
  'Простуда и грипп': '#f0ff00',
  'Сердце и сосуды': '#ff2d78',
  'Пищеварение': '#39ff14',
  'Детское здоровье': '#00f5ff',
};

interface TopArticle {
  slug: string;
  title: string;
  category: string;
  readTime: number;
  views: number;
  image: string;
}

interface TopArticlesProps {
  currentSlug?: string;
}

const RANK_COLORS = ['#f0ff00', '#00f5ff', '#ff6b00', 'rgba(255,255,255,0.35)', 'rgba(255,255,255,0.25)'];

const TopArticles = ({ currentSlug }: TopArticlesProps) => {
  const [topArticles, setTopArticles] = useState<TopArticle[]>([]);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    const viewsMap = getAllArticleViews();
    const hasAnyViews = Object.keys(viewsMap).length > 0;

    if (!hasAnyViews) {
      // No views yet — show most recent articles as fallback
      const fallback = BLOG_ARTICLES
        .filter(a => a.slug !== currentSlug)
        .slice(0, 5)
        .map(a => ({ slug: a.slug, title: a.title, category: a.category, readTime: a.readTime, views: 0, image: a.image }));
      setTopArticles(fallback);
      setIsNew(true);
      return;
    }

    setIsNew(false);

    const ranked = BLOG_ARTICLES
      .map(a => ({ ...a, views: viewsMap[a.slug] ?? 0 }))
      .filter(a => a.slug !== currentSlug)
      .sort((a, b) => b.views - a.views)
      .slice(0, 5)
      .map(a => ({ slug: a.slug, title: a.title, category: a.category, readTime: a.readTime, views: a.views, image: a.image }));

    setTopArticles(ranked);
  }, [currentSlug]);

  if (topArticles.length === 0) return null;

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-1 h-5 rounded-full"
            style={{ background: 'linear-gradient(to bottom, #f0ff00, transparent)' }}
          />
          <span className="font-orbitron font-bold text-xs text-white">
            {isNew ? 'Популярные статьи' : 'Топ читаемых'}
          </span>
        </div>
        {!isNew && (
          <span
            className="flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[9px]"
            style={{ background: 'rgba(240,255,0,0.08)', color: '#f0ff00', border: '1px solid rgba(240,255,0,0.18)' }}
          >
            <i className="ri-bar-chart-2-line" style={{ fontSize: '9px' }} />
            live
          </span>
        )}
      </div>

      {/* Articles list */}
      <div className="space-y-1">
        {topArticles.map((article, idx) => {
          const catColor = CATEGORY_COLORS[article.category] ?? '#ff2d78';
          const rankColor = RANK_COLORS[idx] ?? 'rgba(255,255,255,0.2)';
          const isTop3 = idx < 3;

          return (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-all duration-200 group"
              style={{ border: '1px solid transparent' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)';
                (e.currentTarget as HTMLElement).style.borderColor = `${catColor}18`;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
                (e.currentTarget as HTMLElement).style.borderColor = 'transparent';
              }}
            >
              {/* Rank badge */}
              <div
                className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0 font-orbitron font-black text-[10px]"
                style={{
                  background: isTop3 ? `${rankColor}15` : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${isTop3 ? `${rankColor}35` : 'rgba(255,255,255,0.08)'}`,
                  color: rankColor,
                }}
              >
                {idx + 1}
              </div>

              {/* Thumbnail */}
              <div
                className="w-10 h-10 rounded-lg overflow-hidden shrink-0"
                style={{ border: `1px solid ${catColor}20` }}
              >
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-110"
                />
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1">
                <p className="font-mono text-xs text-white/70 leading-snug line-clamp-2 group-hover:text-white/90 transition-colors">
                  {article.title}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono text-[9px]" style={{ color: `${catColor}80` }}>
                    {article.category}
                  </span>
                  {article.views > 0 && (
                    <>
                      <span className="text-white/15 text-[9px]">·</span>
                      <span className="flex items-center gap-0.5 font-mono text-[9px] text-white/30">
                        <i className="ri-eye-line" style={{ fontSize: '9px' }} />
                        {formatViews(article.views)}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Footer hint */}
      {isNew && (
        <p className="font-mono text-[9px] text-white/20 text-center mt-3 leading-relaxed">
          Рейтинг обновится после просмотра статей
        </p>
      )}
    </div>
  );
};

export default TopArticles;
